/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from 'react-use'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { SearchInput } from '@blade/ui-kit'
import { selectedNetworkAtom } from '@root/entities/network'
import { openTransactionDetailsDialogAction } from '@root/entities/transaction/atoms'
import { WalletModelShort } from '@root/entities/wallet/ui'
import { TransactionHistoryList } from '@root/features/user'
import { reatomUserTransactions } from '@root/features/user/transaction-history'
import { TransactionsFilters } from '@root/features/user/transaction-history/ui/filters/transactions-filters'
import { TransactionHistoryListEmpty } from '@root/features/user/transaction-history/ui/transaction-history-list-empty'
import { walletsManagerModelAtom } from '@root/pages/wallet-manager/atoms'

import { WalletDetailsSection } from '../wallet-details-section'

type Props = {}

export const WalletManagerTransactions: React.FC<Props> = () => {
  const { t } = useTranslation()
  const [network] = useAtom(selectedNetworkAtom)
  const [managerModel] = useAtom(walletsManagerModelAtom)
  const [transactionsWallet] = useAtom(managerModel.transactionsWallet)
  const transactionWalletRef = useLatest(transactionsWallet)
  const model = useMemo(
    () =>
      reatomUserTransactions({
        delugeId: undefined,
        network,
        pairAddress: undefined,
        initialWalletIdsFilter: transactionWalletRef.current
          ? [transactionWalletRef.current.id]
          : undefined,
      }),
    [network, transactionWalletRef],
  )
  const [searchQuery] = useAtom(model.searchQuery)
  const setSearchQuery = useAction(model.setSearch)
  const setWalletIdsFilter = useAction(model.setWalletIdsFilter)
  const openTransactionDetailsDialog = useAction(
    openTransactionDetailsDialogAction,
  )

  const setTransactionsWallet = useAction(managerModel.setTransactionsWallet)
  const handleClose = transactionsWallet
    ? () => setTransactionsWallet(undefined)
    : undefined

  useEffect(() => {
    setWalletIdsFilter(transactionsWallet ? [transactionsWallet.id] : undefined)
  }, [transactionsWallet, setWalletIdsFilter])

  return (
    <Root
      onClose={handleClose}
      title={
        <>
          <div>{t('transactions')}</div>
          {transactionsWallet && (
            <WalletModelShort size="xs" wallet={transactionsWallet} />
          )}
        </>
      }
    >
      <SearchContainer>
        <SearchInput
          size="h40"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
      </SearchContainer>
      <TransactionsFilters model={model} />
      <TransactionHistoryList
        model={model}
        renderEmptyMessage={() => (
          <TransactionHistoryListEmpty
            infoVariant="bold"
            withRetry
            transactionsOwner="current"
          />
        )}
        onOpenTransactionDetails={openTransactionDetailsDialog}
        withWallet={!transactionsWallet}
      />
    </Root>
  )
}

const Root = styled(WalletDetailsSection)`
  ${WalletDetailsSection.Header} {
    padding-block-end: 2px;
  }
  ${WalletDetailsSection.Content} {
    display: grid;
    grid-template-rows: auto auto minmax(0, 1fr);
  }
  ${WalletDetailsSection.Title} {
    justify-content: center;
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    gap: 6px;
  }
`

const SearchContainer = styled.div`
  display: grid;
  gap: 10px;
  padding-block: 0 10px;
  padding-inline: 12px;
`
